/**
 * @fileoverview Component Organism to show customer notes in the Inbox.
 * It allows visualizing the notes associated with the customer of the selected conversation, using optimized architecture and strict separation of logic and presentation.
 *
 * @module CustomerNotesMC
 * @category Component - Organism
 * @since 3.1.0
 * @version 3.1.1
 */
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import LoadingMC from "../../../components/LoadingMC";
import useCustomerNotesData from "../../../hooks/inbox/data/useCustomerNotesData";
import { useTraceUpdate } from "../../../hooks/shared/useDebugRenders";
import ButtonComponent from "../../../components/ButtonMC";
import { sharingInformationService } from "../../../../services/sharingInformationService";

/**
 * CustomerNotesMC - Component to show customer notes in the Inbox.
 */
const CustomerNotesMC = ({ platform }) => {
  const { t, i18n } = useTranslation('InboxMC');
  // Hook Data Layer specialized (separated, optimized)
  const { customerNotes, loading } = useCustomerNotesData(platform, i18n.language);

  useTraceUpdate('CustomerNotesMC', {
    platform,
    notesCount: customerNotes.length,
    loading
  }, {
    logRenderCount: true,
    logPropChanges: true
  });

  // Optimized handler to change to internal note mode
  const handleCreateNote = useCallback(() => {
    sharingInformationService.setSubject({
      handleToggleChatMode: true,
      mode: 'notes'
    });
    sharingInformationService.setSubject({});
  }, []);

  if (loading) {
    return <LoadingMC loading={loading} />;
  }

  return (
    <div className="tw-wrapper tw-flex tw-flex-col tw-mt-2">
      <div className="tw-flex tw-justify-center">
        <ButtonComponent
          outlined
          text={t('chat_note.create')}
          onClick={handleCreateNote}
          className="!tw-bg-white !tw-text-blue-500 tw-text-default tw-font-medium tw-px-8"
        />
      </div>
      {customerNotes.map((note) => (
        <div key={`customer-note-${note.id}`} className="tw-bg-yellow-50 tw-border tw-border-yellow-300 tw-rounded-xl tw-my-2">
          <div className="tw-bg-yellow-100 tw-border-b tw-border-yellow-300 tw-rounded-t-xl tw-text-yellow-950 tw-break-words tw-py-1 tw-px-4 tw-font-normal tw-text-xs">
            {`Por ${note.retailer_user}`}
          </div>
          <div className="tw-rounded-b-xl tw-gap-2 tw-py-2 tw-px-4">
            <p className="tw-text-default tw-text-gray-950 tw-break-words">{note.message}</p>
            <div className="tw-text-gray-500 tw-text-3xs tw-font-normal tw-flex tw-justify-end tw-py-1">
              {note.formattedDate}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default React.memo(CustomerNotesMC);
